import {Box3, Group, Mesh, Object3DEventMap, Vector3} from 'three';
import {isAdjustableLeg} from 'components/customer/RoomPlanner/helpers/calculations';
import {
    PairFacts,
    SnapStrategy,
} from 'components/customer/RoomPlanner/helpers/snapping/types';
import {
    DEFAULT_SNAPPING_DISTANCE,
    END_FACE_EPSILON,
} from 'components/customer/RoomPlanner/helpers/snapping/constants';
import {toCandidate} from 'components/customer/RoomPlanner/helpers/snapping/strategies/candidate';

interface PlanRect {
    minX: number;
    maxX: number;
    minZ: number;
    maxZ: number;
}

interface CornerEndFace {
    axis: 'x' | 'z';
    at: number;
    outward: 1 | -1;
    span0: number;
    span1: number;
}

const footprintRects = (group: Group<Object3DEventMap>): PlanRect[] => {
    const rects: PlanRect[] = [];

    group.updateWorldMatrix(true, true);
    group.traverse((child) => {
        if (!(child instanceof Mesh)) return;
        if (isAdjustableLeg(child)) return;

        const box = new Box3().setFromObject(child);
        if (box.isEmpty()) return;

        rects.push({
            minX: box.min.x,
            maxX: box.max.x,
            minZ: box.min.z,
            maxZ: box.max.z,
        });
    });

    return rects;
};

const mergeIntervals = (
    intervals: Array<[number, number]>
): Array<[number, number]> => {
    const sorted = intervals
        .filter(([start, end]) => end - start > 0.5)
        .sort((a, b) => a[0] - b[0]);
    const merged: Array<[number, number]> = [];

    sorted.forEach(([start, end]) => {
        const last = merged[merged.length - 1];
        if (!last || start > last[1] + END_FACE_EPSILON) {
            merged.push([start, end]);
        } else {
            last[1] = Math.max(last[1], end);
        }
    });

    return merged;
};

const subtractIntervals = (
    source: Array<[number, number]>,
    cut: Array<[number, number]>
): Array<[number, number]> => {
    let remaining = source;

    cut.forEach(([cutStart, cutEnd]) => {
        const next: Array<[number, number]> = [];
        remaining.forEach(([start, end]) => {
            if (cutEnd <= start || cutStart >= end) {
                next.push([start, end]);
                return;
            }
            if (cutStart > start) next.push([start, cutStart]);
            if (cutEnd < end) next.push([cutEnd, end]);
        });
        remaining = next;
    });

    return remaining.filter(([start, end]) => end - start > 5);
};

const clusterEdge = (values: number[], outward: 1 | -1): number[] => {
    const sorted = [...values].sort((a, b) => a - b);
    const groups: number[][] = [];

    sorted.forEach((value) => {
        const last = groups[groups.length - 1];
        if (!last || value - last[last.length - 1] > END_FACE_EPSILON) {
            groups.push([value]);
        } else {
            last.push(value);
        }
    });

    return groups.map((group) =>
        outward > 0 ? Math.max(...group) : Math.min(...group)
    );
};

// Outer ends of both legs, plus the two faces of the notch. Rotation is
// already in the world boxes, so a turned L still exposes its right-hand end.
export const cornerLEndFaces = (
    group: Group<Object3DEventMap>
): CornerEndFace[] => {
    const rects = footprintRects(group);
    if (!rects.length) return [];

    const minX = Math.min(...rects.map((rect) => rect.minX));
    const maxX = Math.max(...rects.map((rect) => rect.maxX));
    const minZ = Math.min(...rects.map((rect) => rect.minZ));
    const maxZ = Math.max(...rects.map((rect) => rect.maxZ));
    const inset = 1;
    const corners = [
        {
            x: minX + inset,
            z: minZ + inset,
            xDir: -1 as const,
            zDir: -1 as const,
        },
        {x: maxX - inset, z: minZ + inset, xDir: 1 as const, zDir: -1 as const},
        {x: minX + inset, z: maxZ - inset, xDir: -1 as const, zDir: 1 as const},
        {x: maxX - inset, z: maxZ - inset, xDir: 1 as const, zDir: 1 as const},
    ];
    const contains = (x: number, z: number) =>
        rects.some(
            (rect) =>
                x >= rect.minX &&
                x <= rect.maxX &&
                z >= rect.minZ &&
                z <= rect.maxZ
        );
    const empty = corners.find((corner) => !contains(corner.x, corner.z));

    const faces: CornerEndFace[] = [];
    const addFaces = (
        axis: 'x' | 'z',
        at: number,
        outward: 1 | -1,
        limitToNotch: boolean
    ) => {
        const onPlane: Array<[number, number]> = [];
        const covering: Array<[number, number]> = [];

        rects.forEach((rect) => {
            const edge =
                axis === 'x'
                    ? outward > 0
                        ? rect.maxX
                        : rect.minX
                    : outward > 0
                      ? rect.maxZ
                      : rect.minZ;
            const span: [number, number] =
                axis === 'x' ? [rect.minZ, rect.maxZ] : [rect.minX, rect.maxX];
            if (Math.abs(edge - at) <= END_FACE_EPSILON) onPlane.push(span);

            const extendsOut =
                axis === 'x'
                    ? outward > 0
                        ? rect.maxX > at + END_FACE_EPSILON
                        : rect.minX < at - END_FACE_EPSILON
                    : outward > 0
                      ? rect.maxZ > at + END_FACE_EPSILON
                      : rect.minZ < at - END_FACE_EPSILON;
            if (extendsOut) covering.push(span);
        });

        let exposed = subtractIntervals(
            mergeIntervals(onPlane),
            mergeIntervals(covering)
        );
        // Notch faces only count where they border the empty corner.
        if (limitToNotch && empty) {
            const midX = (minX + maxX) / 2;
            const midZ = (minZ + maxZ) / 2;
            exposed = exposed.filter(([start, end]) => {
                const mid = (start + end) / 2;
                return axis === 'x'
                    ? empty.zDir > 0
                        ? mid > midZ
                        : mid < midZ
                    : empty.xDir > 0
                      ? mid > midX
                      : mid < midX;
            });
        }

        exposed.forEach(([span0, span1]) => {
            faces.push({axis, at, outward, span0, span1});
        });
    };

    clusterEdge(
        rects.map((rect) => rect.maxX),
        1
    ).forEach((at) =>
        addFaces('x', at, 1, Math.abs(at - maxX) > END_FACE_EPSILON)
    );
    clusterEdge(
        rects.map((rect) => rect.minX),
        -1
    ).forEach((at) =>
        addFaces('x', at, -1, Math.abs(at - minX) > END_FACE_EPSILON)
    );
    clusterEdge(
        rects.map((rect) => rect.maxZ),
        1
    ).forEach((at) =>
        addFaces('z', at, 1, Math.abs(at - maxZ) > END_FACE_EPSILON)
    );
    clusterEdge(
        rects.map((rect) => rect.minZ),
        -1
    ).forEach((at) =>
        addFaces('z', at, -1, Math.abs(at - minZ) > END_FACE_EPSILON)
    );

    return faces;
};

// Where the dragged upper meets the nearest end of a corner L, including
// after the L is rotated. The contact axis meets the end; the position along
// that end is kept, and the tops are kept level.
const findCornerLEndSnap = (
    cabinetGroup: Group<Object3DEventMap>,
    otherProduct: Group<Object3DEventMap>,
    pair: PairFacts,
    targetPos: Vector3,
    currentPos: Vector3
): Vector3 | null => {
    const faces = cornerLEndFaces(otherProduct);
    if (!faces.length) return null;

    const selectedBox = pair.selected.box;
    const otherBox = pair.other.box;
    const delta = targetPos.clone().sub(currentPos);
    const shifted = selectedBox.clone().translate(delta);
    const snappedY =
        cabinetGroup.position.y + (otherBox.max.y - selectedBox.max.y);

    let best: Vector3 | null = null;
    let bestDistance = DEFAULT_SNAPPING_DISTANCE;

    faces.forEach((face) => {
        const contactMin = face.axis === 'x' ? shifted.min.x : shifted.min.z;
        const contactMax = face.axis === 'x' ? shifted.max.x : shifted.max.z;
        const alongMin = face.axis === 'x' ? shifted.min.z : shifted.min.x;
        const alongMax = face.axis === 'x' ? shifted.max.z : shifted.max.x;
        const overlap =
            Math.min(alongMax, face.span1) - Math.max(alongMin, face.span0);
        if (overlap <= 0) return;

        const gap =
            face.outward > 0 ? contactMin - face.at : face.at - contactMax;
        if (gap <= -bestDistance || gap >= bestDistance) return;

        const proposed = targetPos.clone();
        const shift =
            face.outward > 0 ? face.at - contactMin : face.at - contactMax;
        if (face.axis === 'x') proposed.x += shift;
        else proposed.z += shift;
        proposed.y = snappedY;

        const distance = Math.abs(gap);
        if (distance < bestDistance) {
            bestDistance = distance;
            best = proposed;
        }
    });

    return best;
};

// An upper onto any end of an upper L-shaped corner: the outer ends of both
// legs and the two faces of the notch.
export const getCornerLEndSnap: SnapStrategy = (
    cabinetGroup,
    otherProduct,
    ctx,
    pair
) =>
    toCandidate(
        findCornerLEndSnap(
            cabinetGroup,
            otherProduct,
            pair,
            ctx.targetPos,
            ctx.currentPos
        ),
        ctx.targetPos
    );
